import { trackEvent } from './analytics';
import { AnalyticsEventNames, type AnalyticsItem } from './event-map';

export type AnalyticsAuthMethod =
  | 'email'
  | 'google'
  | 'github'
  | 'gitlab'
  | 'microsoft';
export type AnalyticsUploadGuideContext =
  | 'first_bundle'
  | 'additional_bundle';
export type AnalyticsCodeSurface = 'upload_guide' | 'project_config';
export type AnalyticsCommandType =
  | 'cli_login'
  | 'bundle_upload'
  | 'bundle_config';
export type AnalyticsScope = 'organization' | 'project';
export type AnalyticsRole = 'admin' | 'member' | 'viewer' | 'deployer';
export type AnalyticsInvitationDecision = 'accepted' | 'declined';
export type AnalyticsInvitationSource = 'invite_page' | 'notifications';
export type AnalyticsProductionChannelState = 'set' | 'unset';
export type AnalyticsTargetingMode = 'all_users' | 'user_properties';
export type AnalyticsPlanKey =
  | 'free'
  | 'pro'
  | 'pro_plus'
  | 'enterprise'
  | 'other';
export type AnalyticsBillingPeriod = 'month' | 'year';
export type AnalyticsBillingProvider = 'stripe' | 'paddle';
export type AnalyticsAccessSourceType = 'campaign' | 'admin_override';
export type AnalyticsSubscriptionChangeType =
  | 'upgrade'
  | 'downgrade'
  | 'cancel'
  | 'scheduled_change_canceled';

const ALLOWED_PLAN_KEYS = new Set<AnalyticsPlanKey>([
  'free',
  'pro',
  'pro_plus',
  'enterprise',
]);
const ALLOWED_BILLING_PERIODS = new Set<AnalyticsBillingPeriod>(['month', 'year']);
const ALLOWED_BILLING_PROVIDERS = new Set<AnalyticsBillingProvider>([
  'stripe',
  'paddle',
]);
const ALLOWED_ACCESS_SOURCE_TYPES = new Set<AnalyticsAccessSourceType>([
  'campaign',
  'admin_override',
]);
const ALLOWED_SUBSCRIPTION_CHANGE_TYPES = new Set<AnalyticsSubscriptionChangeType>([
  'upgrade',
  'downgrade',
  'cancel',
  'scheduled_change_canceled',
]);

const sanitizePlanKey = (planKey: string): AnalyticsPlanKey =>
  ALLOWED_PLAN_KEYS.has(planKey as AnalyticsPlanKey)
    ? (planKey as AnalyticsPlanKey)
    : 'other';

export const trackLogin = ({ method }: { method: AnalyticsAuthMethod }) =>
  trackEvent(AnalyticsEventNames.LOGIN, { method });

export const trackSignUp = ({ method }: { method: AnalyticsAuthMethod }) =>
  trackEvent(AnalyticsEventNames.SIGN_UP, { method });

export const trackAccountActivated = () =>
  trackEvent(AnalyticsEventNames.ACCOUNT_ACTIVATED);

export const trackOrganizationCreated = () =>
  trackEvent(AnalyticsEventNames.ORGANIZATION_CREATED);

export const trackProjectCreated = () =>
  trackEvent(AnalyticsEventNames.PROJECT_CREATED);

export const trackChannelCreated = () =>
  trackEvent(AnalyticsEventNames.CHANNEL_CREATED);

export const trackCliAuthorized = () =>
  trackEvent(AnalyticsEventNames.CLI_AUTHORIZED);

export const trackUploadGuideOpened = ({
  context,
}: {
  context: AnalyticsUploadGuideContext;
}) => trackEvent(AnalyticsEventNames.UPLOAD_GUIDE_OPENED, { context });

export const trackCopyCode = ({
  surface,
  commandType,
}: {
  surface: AnalyticsCodeSurface;
  commandType: AnalyticsCommandType;
}) =>
  trackEvent(AnalyticsEventNames.COPY_CODE, {
    surface,
    command_type: commandType,
  });

export const trackSelectUploadDocumentation = () =>
  trackEvent(AnalyticsEventNames.SELECT_CONTENT, {
    content_type: 'documentation',
    item_id: 'bundle_upload',
  });

export const trackMemberInvited = ({
  scope,
  role,
}: {
  scope: AnalyticsScope;
  role: AnalyticsRole;
}) => trackEvent(AnalyticsEventNames.MEMBER_INVITED, { scope, role });

export const trackInvitationResponded = ({
  scope,
  decision,
  source,
}: {
  scope: AnalyticsScope;
  decision: AnalyticsInvitationDecision;
  source: AnalyticsInvitationSource;
}) =>
  trackEvent(AnalyticsEventNames.INVITATION_RESPONDED, {
    scope,
    decision,
    source,
  });

export const trackProductionChannelChanged = ({
  state,
}: {
  state: AnalyticsProductionChannelState;
}) => trackEvent(AnalyticsEventNames.PRODUCTION_CHANNEL_CHANGED, { state });

export const trackManagedPublishingChanged = ({
  enabled,
}: {
  enabled: boolean;
}) => trackEvent(AnalyticsEventNames.MANAGED_PUBLISHING_CHANGED, { enabled });

export const trackBundleRolloutUpdated = ({
  rolloutPercentage,
  targetingMode,
}: {
  rolloutPercentage: number;
  targetingMode: AnalyticsTargetingMode;
}) =>
  trackEvent(AnalyticsEventNames.BUNDLE_ROLLOUT_UPDATED, {
    rollout_percentage: rolloutPercentage,
    targeting_mode: targetingMode,
  });

export const trackBundleRollbackCompleted = () =>
  trackEvent(AnalyticsEventNames.BUNDLE_ROLLBACK_COMPLETED);

export const trackBeginCheckout = ({
  planKey,
  billingPeriod,
  provider,
  currency,
  value,
}: {
  planKey: string;
  billingPeriod: AnalyticsBillingPeriod;
  provider: AnalyticsBillingProvider;
  currency: string;
  value: number;
}) => {
  if (
    typeof planKey !== 'string' ||
    typeof currency !== 'string' ||
    !ALLOWED_BILLING_PERIODS.has(billingPeriod) ||
    !ALLOWED_BILLING_PROVIDERS.has(provider)
  ) {
    return Promise.resolve();
  }

  const normalizedCurrency = currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(normalizedCurrency) || !Number.isFinite(value) || value < 0) {
    return Promise.resolve();
  }

  const safePlanKey = sanitizePlanKey(planKey);
  const items: AnalyticsItem[] = [
    {
      item_id: safePlanKey,
      item_name: safePlanKey,
      price: value,
      quantity: 1,
    },
  ];

  return trackEvent(AnalyticsEventNames.BEGIN_CHECKOUT, {
    currency: normalizedCurrency,
    value,
    items,
    billing_period: billingPeriod,
    provider,
  });
};

export const trackAccessCodeRedeemed = ({
  planKey,
  sourceType,
}: {
  planKey: string;
  sourceType: AnalyticsAccessSourceType;
}) => {
  if (typeof planKey !== 'string' || !ALLOWED_ACCESS_SOURCE_TYPES.has(sourceType)) {
    return Promise.resolve();
  }

  return trackEvent(AnalyticsEventNames.ACCESS_CODE_REDEEMED, {
    plan_key: sanitizePlanKey(planKey),
    source_type: sourceType,
  });
};

export const trackSubscriptionChanged = ({
  changeType,
  targetPlan,
  provider,
  billingPeriod,
}: {
  changeType: AnalyticsSubscriptionChangeType;
  targetPlan: string;
  provider: AnalyticsBillingProvider;
  billingPeriod?: AnalyticsBillingPeriod;
}) => {
  if (
    typeof targetPlan !== 'string' ||
    !ALLOWED_SUBSCRIPTION_CHANGE_TYPES.has(changeType) ||
    !ALLOWED_BILLING_PROVIDERS.has(provider) ||
    (billingPeriod !== undefined && !ALLOWED_BILLING_PERIODS.has(billingPeriod))
  ) {
    return Promise.resolve();
  }

  return trackEvent(AnalyticsEventNames.SUBSCRIPTION_CHANGED, {
    change_type: changeType,
    target_plan: sanitizePlanKey(targetPlan),
    provider,
    billing_period: billingPeriod,
  });
};
